import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, tap } from 'rxjs/operators';

import { isMsalRedirectError } from '../auth/msal-token.service';

import { environment } from '../../../environments/environment';
import { HttpFlightService } from './http-flight.service';
import { SUPPRESS_GLOBAL_HTTP_ERROR_SNACK } from './http-global-error-snackbar.tokens';

/** Shown when the API does not return a usable `message` / `detail` (etc.) on error. */
export const DEFAULT_GLOBAL_HTTP_ERROR_MESSAGE =
  "We're sorry, something went wrong. Please try again.";

/** Avoid double toasts when a feature and this interceptor react to the same failure. */
let lastSnackDedupeKey = '';
let lastSnackDedupeAt = 0;
const SNACK_DEDUPE_MS = 750;

function shouldEmitSnackAfterDedupe(dedupeKey: string): boolean {
  const now = Date.now();
  if (dedupeKey === lastSnackDedupeKey && now - lastSnackDedupeAt < SNACK_DEDUPE_MS) {
    return false;
  }
  lastSnackDedupeKey = dedupeKey;
  lastSnackDedupeAt = now;
  return true;
}

function apiBasePrefix(): string {
  return environment.apiBaseUrl.replace(/\/$/, '');
}

function isSpiceBackendRequest(url: string): boolean {
  const base = apiBasePrefix();
  return url.startsWith(base);
}

function refreshApiPath(): string {
  return typeof environment.refreshApiPath === 'string' && environment.refreshApiPath.trim()
    ? environment.refreshApiPath.trim()
    : '/api/v1/refresh';
}

/** Avoid circular DI (AuthService injects HttpClient). Paths must stay in sync with {@link AuthService}. */
function isLoginOrRefreshUrl(url: string): boolean {
  return url.includes('/api/v1/login') || url.includes(refreshApiPath());
}

function shouldHandle(req: HttpRequest<unknown>): boolean {
  if (req.context.get(SUPPRESS_GLOBAL_HTTP_ERROR_SNACK)) {
    return false;
  }
  if (!isSpiceBackendRequest(req.url)) {
    return false;
  }
  if (isLoginOrRefreshUrl(req.url)) {
    return false;
  }
  return true;
}

/**
 * Best-effort message from JSON error bodies (and plain-text errors).
 * Returns null when nothing user-facing is defined — caller should use {@link DEFAULT_GLOBAL_HTTP_ERROR_MESSAGE}.
 */
export function extractSpiceApiErrorMessage(err: HttpErrorResponse): string | null {
  const body = err.error;
  if (body == null) {
    return null;
  }
  if (typeof body === 'string') {
    const t = body.trim();
    if (!t) return null;
    if (t.startsWith('{') || t.startsWith('[')) {
      try {
        const parsed = JSON.parse(t) as unknown;
        return extractFromUnknownBody(parsed);
      } catch {
        return t.length > 400 ? `${t.slice(0, 400)}…` : t;
      }
    }
    return t.length > 400 ? `${t.slice(0, 400)}…` : t;
  }
  return extractFromUnknownBody(body);
}

function extractFromUnknownBody(body: unknown): string | null {
  if (body == null || typeof body !== 'object' || Array.isArray(body)) {
    return null;
  }
  const o = body as Record<string, unknown>;
  for (const key of ['message', 'detail', 'error_description', 'error'] as const) {
    const v = o[key];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  const errors = o['errors'];
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0];
    if (typeof first === 'string' && first.trim()) {
      return first.trim();
    }
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      const msg = (first as Record<string, unknown>)['message'];
      if (typeof msg === 'string' && msg.trim()) {
        return msg.trim();
      }
    }
  }
  return null;
}

function shouldShowSnackForStatus(status: number): boolean {
  if (status === 401) {
    return false;
  }
  if (status === 0) {
    return true;
  }
  return status >= 400;
}

/**
 * When a SPICE API call fails while a request is in flight (loader semantics), opens a red snackbar
 * top-end with the server `message` when present, otherwise {@link DEFAULT_GLOBAL_HTTP_ERROR_MESSAGE}.
 *
 * Suppress for a call via {@link SUPPRESS_GLOBAL_HTTP_ERROR_SNACK} / {@link httpContextSuppressGlobalHttpErrorSnack}
 * when the feature already shows its own error.
 */
export const httpGlobalErrorSnackInterceptor: HttpInterceptorFn = (req, next) => {
  if (!shouldHandle(req)) {
    return next(req);
  }

  const flight = inject(HttpFlightService);
  const snackBar = inject(MatSnackBar);

  flight.begin();
  return next(req).pipe(
    tap({
      error: (err: unknown) => {
        if (isMsalRedirectError(err)) {
          return;
        }
        if (!(err instanceof HttpErrorResponse)) {
          return;
        }
        if (!shouldShowSnackForStatus(err.status)) {
          return;
        }
        /** At least this request was in flight — matches “API failed while loading”. */
        if (flight.activeCount() < 1) {
          return;
        }
        const defined = extractSpiceApiErrorMessage(err);
        const text = (defined?.trim().length ? defined.trim() : null) ?? DEFAULT_GLOBAL_HTTP_ERROR_MESSAGE;
        const dedupeKey = `${req.method}\0${req.url}\0${err.status}\0${text}`;
        if (!shouldEmitSnackAfterDedupe(dedupeKey)) {
          return;
        }
        snackBar.open(text, '\u2715', {
          duration: 9000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['spice-http-error-snackbar']
        });
      }
    }),
    finalize(() => flight.end())
  );
};
